import { api } from "@/common/lib/api";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { AccountRole } from "./accountsApi";

export type InvitationStatus =
  | "pending"
  | "accepted"
  | "rejected"
  | "cancelled"
  | "auto_cancelled"
  | "expired";

export type Invitation = {
  id: string;
  accountId: string;
  email: string;
  role: AccountRole;
  status: InvitationStatus;
  expiresAt: string;
  createdAt: string;
  invitedBy: string;
};

export type InvitationPreview = {
  id: string;
  email: string;
  role: AccountRole;
  status: InvitationStatus;
  expiresAt: string;
  account: { id: string; name: string };
  inviter: { email: string };
};

export type AcceptInvitationResponse = {
  status: "accepted" | "already_member";
  accountId: string;
};

export function useInvitationsList(accountId: string) {
  return useQuery<Invitation[]>({
    queryKey: ["accounts", accountId, "invitations"],
    queryFn: () => api.get<Invitation[]>(`/accounts/${accountId}/invitations`),
    enabled: !!accountId,
  });
}

export function useCreateInvitation(accountId: string) {
  const qc = useQueryClient();
  return useMutation<{ status: string }, Error, { email: string; role: AccountRole }>({
    mutationFn: (input) =>
      api.post<{ status: string }>(`/accounts/${accountId}/invitations`, input),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["accounts", accountId, "invitations"] });
    },
  });
}

export function useCancelInvitation(accountId: string) {
  const qc = useQueryClient();
  return useMutation<void, Error, string>({
    mutationFn: (invitationId) =>
      api.delete<void>(`/accounts/${accountId}/invitations/${invitationId}`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["accounts", accountId, "invitations"] });
    },
  });
}

export function useResendInvitation(accountId: string) {
  const qc = useQueryClient();
  return useMutation<void, Error, string>({
    mutationFn: (invitationId) =>
      api.post<void>(`/accounts/${accountId}/invitations/${invitationId}/resend`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["accounts", accountId, "invitations"] });
    },
  });
}

export function useInvitationPreview(token: string | null) {
  return useQuery<InvitationPreview>({
    queryKey: ["invitation-preview", token],
    queryFn: () => api.get<InvitationPreview>(`/invitations/${token}`),
    enabled: !!token,
    retry: false,
  });
}

export function useAcceptInvitation() {
  return useMutation<AcceptInvitationResponse, Error, string>({
    mutationFn: (token) => api.post<AcceptInvitationResponse>(`/invitations/${token}/accept`),
  });
}

export function useRejectInvitation() {
  return useMutation<void, Error, string>({
    mutationFn: (token) => api.post<void>(`/invitations/${token}/reject`),
  });
}
